import { useEffect, useState, useCallback } from "react";
import { Search } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { SERVICES } from "../../lib/site";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";

const BOOKING_STATUSES = ["NEW", "CONTACTED", "BOOKED", "COMPLETED", "CANCELLED"];

const statusChip = (s) =>
  s === "NEW" ? "bg-sky/15 text-sky border border-sky/30"
  : s === "BOOKED" ? "bg-emerald-600/15 text-emerald-400 border border-emerald-600/30"
  : s === "CANCELLED" ? "bg-slate-500/15 text-slate-400 border border-slate-500/30"
  : "bg-deep/60 text-pale/70 border border-deep";

const fmtDT = (iso) => (iso ? new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "-");

export default function Bookings() {
  const [bookings, setBookings] = useState([]);
  const [q, setQ] = useState("");
  const [service, setService] = useState("all");
  const [selected, setSelected] = useState(null);
  const [notes, setNotes] = useState("");

  const load = useCallback(() => {
    const params = {};
    if (q) params.q = q;
    if (service !== "all") params.service = service;
    api.get("/admin/bookings", { params }).then((res) => setBookings(res.data)).catch(() => {});
  }, [q, service]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const openBooking = (b) => {
    setSelected(b);
    setNotes(b.notes || "");
  };

  const updateStatus = async (b, status) => {
    try {
      await api.put(`/admin/bookings/${b.id}`, { status });
      toast.success(`Booking marked ${status}.`);
      setSelected(null);
      load();
    } catch (e) {
      toast.error("Could not update booking.");
    }
  };

  const saveNotes = async () => {
    try {
      await api.put(`/admin/bookings/${selected.id}`, { notes });
      toast.success("Notes saved.");
      setSelected(null);
      load();
    } catch (e) {
      toast.error("Could not save notes.");
    }
  };

  return (
    <div data-testid="admin-bookings">
      <h1 className="font-heading font-extrabold text-white text-2xl tracking-tight">Bookings</h1>
      <p className="text-pale/50 text-sm mt-1">{bookings.length} booking request(s).</p>

      <div className="flex flex-col sm:flex-row gap-3 mt-6">
        <div className="relative flex-1 max-w-md">
          <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-pale/40" />
          <Input data-testid="bookings-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, phone, reg..." className="pl-10 bg-navy border-deep text-white h-10" />
        </div>
        <Select value={service} onValueChange={setService}>
          <SelectTrigger data-testid="bookings-service-filter" className="sm:w-64 bg-navy border-deep text-white h-10">
            <SelectValue placeholder="All services" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All services</SelectItem>
            {SERVICES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="mt-5 bg-navy border border-deep rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="bookings-table">
            <thead>
              <tr className="text-left text-pale/40 text-xs uppercase tracking-wider border-b border-deep">
                <th className="px-4 py-3 font-heading">Customer</th>
                <th className="px-4 py-3 font-heading">Vehicle</th>
                <th className="px-4 py-3 font-heading">Service</th>
                <th className="px-4 py-3 font-heading">Requested</th>
                <th className="px-4 py-3 font-heading">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-deep/60">
              {bookings.map((b) => (
                <tr key={b.id} data-testid={`booking-row-${b.id}`} onClick={() => openBooking(b)} className="text-pale/80 cursor-pointer hover:bg-deep/40 transition-colors">
                  <td className="px-4 py-3.5">
                    <p className="font-medium text-white">{b.name}</p>
                    <p className="text-xs text-pale/40">{b.phone}</p>
                  </td>
                  <td className="px-4 py-3.5">
                    <span className="font-mono text-xs bg-deep/70 px-2 py-1 rounded">{b.vehicle_reg || "-"}</span>
                    <p className="text-xs text-pale/40 mt-1">{[b.vehicle_make, b.vehicle_model].filter(Boolean).join(" ")}</p>
                  </td>
                  <td className="px-4 py-3.5">{b.service}</td>
                  <td className="px-4 py-3.5 text-pale/60">{b.preferred_date || "-"} {b.preferred_time && b.preferred_time !== "Any" ? `(${b.preferred_time})` : ""}</td>
                  <td className="px-4 py-3.5"><span className={`status-chip ${statusChip(b.status)}`}>{b.status}</span></td>
                </tr>
              ))}
              {bookings.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-10 text-center text-pale/40">No bookings found.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={Boolean(selected)} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="sm:max-w-lg bg-navy border-deep text-pale">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading font-extrabold text-white" data-testid="booking-detail-title">{selected.name} - {selected.service}</DialogTitle>
              </DialogHeader>
              <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm mt-1">
                <div><p className="text-pale/40 text-xs uppercase tracking-wider">Phone</p><p className="text-white mt-0.5">{selected.phone}</p></div>
                <div><p className="text-pale/40 text-xs uppercase tracking-wider">Email</p><p className="text-white mt-0.5 break-all">{selected.email}</p></div>
                <div><p className="text-pale/40 text-xs uppercase tracking-wider">Registration</p><p className="font-mono text-white mt-0.5">{selected.vehicle_reg || "-"}</p></div>
                <div><p className="text-pale/40 text-xs uppercase tracking-wider">Vehicle</p><p className="text-white mt-0.5">{[selected.vehicle_make, selected.vehicle_model].filter(Boolean).join(" ") || "-"}</p></div>
                <div><p className="text-pale/40 text-xs uppercase tracking-wider">Requested</p><p className="text-white mt-0.5">{selected.preferred_date || "-"} {selected.preferred_time ? `(${selected.preferred_time})` : ""}</p></div>
                <div><p className="text-pale/40 text-xs uppercase tracking-wider">Received</p><p className="text-white mt-0.5">{fmtDT(selected.created_at)}</p></div>
              </div>
              {selected.info && (
                <div className="mt-4">
                  <p className="text-pale/40 text-xs uppercase tracking-wider">Additional information</p>
                  <p className="text-sm text-pale/80 mt-1 bg-deep/40 rounded p-3">{selected.info}</p>
                </div>
              )}
              <div className="mt-4">
                <p className="text-pale/40 text-xs uppercase tracking-wider mb-1.5">Internal notes</p>
                <Textarea data-testid="booking-notes" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} className="bg-deep/40 border-deep text-white" placeholder="Notes only visible here..." />
                <button onClick={saveNotes} data-testid="booking-save-notes" className="mt-2 text-xs font-heading font-bold text-sky hover:text-white tracking-wide transition-colors">SAVE NOTES</button>
              </div>
              <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-deep">
                {BOOKING_STATUSES.map((s) => (
                  <button
                    key={s}
                    onClick={() => updateStatus(selected, s)}
                    data-testid={`booking-status-${s.toLowerCase()}`}
                    className={`status-chip !cursor-pointer ${selected.status === s ? statusChip(s) : "bg-deep/40 text-pale/50 border border-deep hover:text-white"}`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
